import React, { useState, useEffect } from "react";
import { useAuth } from "../../../../Security/AuthProvider";
import "../CreateForm.css";
import { useNotification } from '../../../../Utils/Commons/Notification/NotificationContext';

function ModifyForm({ fetchForms, setShowModifyPopup, form, updateSelectedForm }) {
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [fields, setFields] = useState([]);
  const [currentField, setCurrentField] = useState({
    name: "",
    label: "",
    type: "text",
    placeholder: "",
    required: false,
    options: [],
  });
  const [currentOption, setCurrentOption] = useState({
    value: "",
    label: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { getCredentials } = useAuth();

  useEffect(() => {
    if (form) {
      setFormName(form.name || "");
      setFormDescription(form.description || "");

      // Map database fields to frontend format
      const mappedFields =
        form.fields?.map((field) => ({
          id: field.id,
          name: field.field_name,
          label: field.field_label,
          type: field.field_type,
          placeholder: field.field_placeholder || "",
          required: field.is_required === 1,
          options:
            field.options?.map((opt) => ({
              value: opt.option_value,
              label: opt.option_label,
            })) || [],
        })) || [];

      setFields(mappedFields);
      console.log("Mapped fields:", mappedFields);
    }
  }, [form]);

  const fieldTypes = [
    { value: "text", label: "Champ de texte" },
    { value: "checkbox", label: "Choix multiple" },
    { value: "radio", label: "Choix unique" },
    { value: "image", label: "Envoie d'image" },
  ];

  const handleAddField = () => {
    if (!currentField.name || !currentField.label) {
      alert("Le nom et le label du champ sont requis");
      return;
    }

    // Generate a safe field name if not provided
    const fieldName = currentField.name.replace(/\s+/g, "_").toLowerCase();

    // Check for duplicate field names
    if (fields.some((field) => field.name === fieldName)) {
      alert("Le nom du champ doit être unique");
      return;
    }

    // Create a new field object without an ID - this signals to the backend that it's a new field
    const newField = { 
      ...currentField, 
      name: fieldName,
      // Make sure we don't accidentally include an id property
      id: undefined 
    };
    
    console.log("Adding new field:", newField);
    setFields([...fields, newField]);
    
    // Reset the current field form
    setCurrentField({
      name: "",
      label: "",
      type: "text",
      placeholder: "",
      required: false,
      options: [],
    });
  };

  const handleAddOption = () => {
    if (!currentOption.value || !currentOption.label) {
      alert("La valeur et le label de l'option sont requis");
      return;
    }

    setCurrentField({
      ...currentField,
      options: [...currentField.options, { ...currentOption }],
    });
    setCurrentOption({ value: "", label: "" });
  };

  const handleRemoveField = (index) => {
    console.log("Removing field at index:", index, "Field:", fields[index]);
    const updatedFields = [...fields];
    updatedFields.splice(index, 1);
    console.log("Fields after removal:", updatedFields);
    setFields(updatedFields);
  };

  const handleRemoveOption = (index) => {
    const updatedOptions = [...currentField.options];
    updatedOptions.splice(index, 1);
    setCurrentField({
      ...currentField,
      options: updatedOptions,
    });
  };

  const handleMoveField = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === fields.length - 1)
    ) {
      return;
    }

    const updatedFields = [...fields];
    const swapIndex = direction === "up" ? index - 1 : index + 1;

    [updatedFields[index], updatedFields[swapIndex]] = [
      updatedFields[swapIndex],
      updatedFields[index],
    ];

    setFields(updatedFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!formName) {
      setError("Le nom du formulaire est requis");
      return;
    }

    if (fields.length === 0) {
      setError("Ajouter au moins une question à votre formulaire");
      return;
    }

    try {
      setLoading(true);
      
      console.log("Submitting fields:", fields);

      // Format fields to match what the backend expects
      const formattedFields = fields.map(field => {
        console.log("Formatting field for submission:", field);
        
        const formattedField = {
          // Only include ID if it exists (for existing fields)
          ...(field.id && { id: field.id }),
          field_type: field.type,
          field_label: field.label,
          field_placeholder: field.placeholder || '',
          is_required: field.required ? 1 : 0,
          options: field.options ? field.options.map(opt => ({
            option_value: opt.value,
            option_label: opt.label
          })) : []
        };
        
        console.log("Formatted field:", formattedField);
        return formattedField;
      });
      
      console.log("Formatted fields:", formattedFields);

      const response = await fetch(`/forms/${form.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formName,
          description: formDescription,
          fields: formattedFields,
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Échec de la modification du formulaire"
        );
      }

      // Form updated successfully
      await fetchForms();
      
      // Mettre à jour l'affichage immédiat du formulaire modifié
      if (updateSelectedForm) {
        await updateSelectedForm(form.id);
      }
      
      setShowModifyPopup(null);
    } catch (err) {
      setError(err.message);
      console.error("Error updating form:", err);
    } finally {
      setLoading(false);
    }
  };

  const needsOptions = ["radio", "checkbox", "select"].includes(
    currentField.type
  );

  return (
    <div className="form-creation-popup" onClick={(e) => e.stopPropagation()}>
      <h2>Modifier le formulaire</h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Détails du formulaire</h3>
          <div className="form-group">
            <label>Nom du formulaire *</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Entrez le nom du formulaire"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Entrez la description du formulaire (optionnel)"
              rows="3"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Champs du formulaire</h3>

          {fields.length > 0 && (
            <div className="fields-list">
              {fields.map((field, index) => (
                <div key={index} className="field-item">
                  <div className="field-header">
                    <h4>{field.label}</h4>
                    <div className="field-actions">
                      <button
                        type="button"
                        className="move-btn"
                        onClick={() => handleMoveField(index, "up")}
                        disabled={index === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="move-btn"
                        onClick={() => handleMoveField(index, "down")}
                        disabled={index === fields.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => handleRemoveField(index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>

                  <div className="field-details">
                    <p>
                      <strong>Type:</strong>{" "}
                      {fieldTypes.find((t) => t.value === field.type)?.label ||
                        field.type}
                    </p>
                    <p>
                      <strong>Nom:</strong> {field.name}
                    </p>
                    {field.placeholder && (
                      <p>
                        <strong>Placeholder:</strong> {field.placeholder}
                      </p>
                    )}
                    <p>
                      <strong>Obligatoire:</strong>{" "}
                      {field.required ? "Oui" : "Non"}
                    </p>

                    {field.options && field.options.length > 0 && (
                      <div className="field-options">
                        <p>
                          <strong>Options:</strong>
                        </p>
                        <ul>
                          {field.options.map((option, optIdx) => (
                            <li key={optIdx}>
                              {option.label} ({option.value})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="add-field-section">
            <h4>Ajouter un nouveau champ</h4>
            <div className="field-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Libellé du champ *</label>
                  <input
                    type="text"
                    value={currentField.label}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        label: e.target.value,
                      })
                    }
                    placeholder="ex: Êtes-vous arrivé à l'heure ?"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de l'information *</label>
                  <input
                    type="text"
                    value={currentField.name}
                    onChange={(e) =>
                      setCurrentField({ ...currentField, name: e.target.value })
                    }
                    placeholder="ex: Retard :"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type de champ</label>
                  <select
                    value={currentField.type}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        type: e.target.value,
                        options: ["radio", "checkbox", "select"].includes(
                          e.target.value
                        )
                          ? currentField.options
                          : [],
                      })
                    }
                  >
                    {fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Placeholder (optionnel)</label>
                  <input
                    type="text"
                    value={currentField.placeholder || ""}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        placeholder: e.target.value,
                      })
                    }
                    placeholder="ex: Oui/Non"
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={currentField.required}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        required: e.target.checked,
                      })
                    }
                  />
                  Cocher si obligatoire
                </label>
              </div>

              {needsOptions && (
                <div className="options-section">
                  <h5>Options du champ</h5>

                  {currentField.options.length > 0 && (
                    <div className="options-list">
                      {currentField.options.map((option, idx) => (
                        <div key={idx} className="option-item">
                          <span>
                            {option.label} ({option.value})
                          </span>
                          <button
                            type="button"
                            className="remove-option-btn"
                            onClick={() => handleRemoveOption(idx)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="add-option-form">
                    <div className="form-row">
                      <div className="form-group">
                        <input
                          type="text"
                          value={currentOption.label}
                          onChange={(e) =>
                            setCurrentOption({
                              ...currentOption,
                              label: e.target.value,
                            })
                          }
                          placeholder="Réponse"
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          value={currentOption.value}
                          onChange={(e) =>
                            setCurrentOption({
                              ...currentOption,
                              value: e.target.value,
                            })
                          }
                          placeholder="Valeur de la réponse"
                        />
                      </div>
                      <button
                        type="button"
                        className="add-option-btn"
                        onClick={handleAddOption}
                      >
                        Ajouter l'option
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="add-field-btn"
                onClick={handleAddField}
              >
                Ajouter le champ
              </button>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => setShowModifyPopup(null)}
          >
            Annuler
          </button>
          <button type="submit" className="save-btn" disabled={loading}>
            {loading ? "Mise à jour..." : "Mettre à jour le formulaire"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ModifyForm;
